import React, { useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Category as ProductsIcon,
  Search as LookupIcon,
  Memory as CPUIcon,
  History as ActivityIcon,
  People as UsersIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../services/auth';
import UserProfileModal from './UserProfileModal';

const drawerWidth = 280;
const mobileDrawerWidth = 280;

const menuItems = [
  {
    text: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard',
    description: 'Overview & Statistics'
  },
  {
    text: 'Products',
    icon: <ProductsIcon />,
    path: '/products',
    description: 'Product Management'
  },
  {
    text: 'Inventory',
    icon: <InventoryIcon />,
    path: '/inventory',
    description: 'Stock Management'
  },
  {
    text: 'Lookup',
    icon: <LookupIcon />,
    path: '/lookup',
    description: 'Supplier Parts Lookup'
  },
  {
    text: 'Activity Log',
    icon: <ActivityIcon />,
    path: '/activity-log',
    description: 'Inventory Movement History'
  },
];

function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, canManageUsers, signOut } = useAuth();

  // Dynamic menu items based on user role
  const getMenuItems = () => {
    const items = [...menuItems];

    // Users menu item removed from sidebar - accessible only from user dropdown

    return items;
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await signOut();
    handleUserMenuClose();
    navigate('/');
  };

  const handleProfileOpen = () => {
    setProfileModalOpen(true);
    handleUserMenuClose();
  };

  const handleUserManagement = () => {
    navigate('/users');
    handleUserMenuClose();
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Brand Section */}
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #00BFFF 0%, #0080FF 100%)',
          borderBottom: '2px solid rgba(0, 191, 255, 0.3)',
        }}
      >
        <CPUIcon sx={{ fontSize: 32, mr: 2, color: 'white' }} />
        <Box>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
            MJTech
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            Inventory System
          </Typography>
        </Box>
      </Box>

      {/* Navigation Menu */}
      <List sx={{ flexGrow: 1, px: 1, py: 2 }}>
        {getMenuItems().map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => handleMenuClick(item.path)}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  py: 1.5,
                  background: isActive
                    ? 'linear-gradient(135deg, rgba(0, 191, 255, 0.2) 0%, rgba(0, 128, 255, 0.2) 100%)'
                    : 'transparent',
                  border: isActive ? '1px solid rgba(0, 191, 255, 0.3)' : '1px solid transparent',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(0, 191, 255, 0.1) 0%, rgba(0, 128, 255, 0.1) 100%)',
                    border: '1px solid rgba(0, 191, 255, 0.2)',
                    transform: 'translateX(4px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  secondary={item.description}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
                  }}
                  secondaryTypographyProps={{
                    fontSize: '0.75rem',
                    color: theme.palette.text.secondary,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Footer/System Info */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid rgba(0, 191, 255, 0.1)',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          © 2025 MJTech Inventory
        </Typography>
        <br />
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, opacity: 0.7 }}>
          v1.0.0 Beta
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />

          {/* User Profile Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            {!isSmallMobile && (
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  background: 'rgba(0, 191, 255, 0.1)',
                  px: { sm: 2, md: 2 },
                  py: 1,
                  borderRadius: 2,
                  border: '1px solid rgba(0, 191, 255, 0.3)',
                  maxWidth: { sm: '150px', md: 'none' },
                  overflow: 'hidden',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: { sm: '0.8rem', md: '0.875rem' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {user?.Role?.toUpperCase()} - {user?.FullName}
                </Typography>
              </Box>
            )}

            <IconButton
              size="large"
              onClick={handleUserMenuOpen}
              sx={{
                color: 'white',
                p: { xs: 1, sm: 1.5 }
              }}
            >
              <Avatar
                sx={{
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                }}
              >
                {user?.FullName?.charAt(0)?.toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: { xs: '100vw', sm: mobileDrawerWidth },
              background: 'linear-gradient(180deg, #1A1F2E 0%, #0A0E1A 100%)',
              borderRight: '2px solid rgba(0, 191, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 191, 255, 0.1)',
              // Touch-friendly spacing
              '& .MuiListItemButton-root': {
                minHeight: '56px',
                paddingY: 2,
              },
              // Custom scrollbar styling
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0, 191, 255, 0.1)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'linear-gradient(45deg, #00BFFF 30%, #0080FF 90%)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'linear-gradient(45deg, #0080FF 30%, #00BFFF 90%)',
                },
              },
              // Firefox scrollbar styling
              scrollbarWidth: 'thin',
              scrollbarColor: '#00BFFF rgba(0, 191, 255, 0.1)',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: 'linear-gradient(180deg, #1A1F2E 0%, #0A0E1A 100%)',
              borderRight: '2px solid rgba(0, 191, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 191, 255, 0.1)',
              // Custom scrollbar styling
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0, 191, 255, 0.1)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'linear-gradient(45deg, #00BFFF 30%, #0080FF 90%)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'linear-gradient(45deg, #0080FF 30%, #00BFFF 90%)',
                },
              },
              // Firefox scrollbar styling
              scrollbarWidth: 'thin',
              scrollbarColor: '#00BFFF rgba(0, 191, 255, 0.1)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          overflow: 'hidden',
        }}
      >
        <Toolbar />
        <Box
          sx={{
            p: { xs: 1, sm: 2, md: 3 },
            height: 'calc(100vh - 64px)',
            overflow: 'auto',
            // Smooth scrolling for mobile
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {children}
        </Box>
      </Box>

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleUserMenuClose}
        onClick={handleUserMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleProfileOpen}>
          <AccountIcon sx={{ mr: 2 }} />
          Profile & Settings
        </MenuItem>
        {canManageUsers() && (
          <MenuItem onClick={handleUserManagement}>
            <UsersIcon sx={{ mr: 2 }} />
            User Management
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 2 }} />
          Logout
        </MenuItem>
      </Menu>

      {/* User Profile Modal */}
      <UserProfileModal
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
      />
    </Box>
  );
}

export default Layout;